import React, { useEffect, useState } from 'react';
import {
    Box,
    Button,
    CircularProgress,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    FormHelperText,
    Typography,
} from '@material-ui/core';
import { useNotify, useTranslate } from 'react-admin';
import { useSelector } from 'react-redux';
import { useIsMobile } from '../../hooks/useIsMobile';
import { CustomReducerState } from '../../redux/reducers';
import { updatePrestataireAffiliation } from './api';
import TypesAndDepartmentsFields from './TypesAndDepartmentsFields';
import { PrestataireListRow, PrestataireScope, scopesAreValid } from './types';
import { uploadHomologationPdf } from './uploadHomologationPdf';
import { formatCompanyAddress } from './ignGeocode';

interface EditPrestataireDialogProps {
    open: boolean;
    row: PrestataireListRow | null;
    onClose: () => void;
    onSuccess: () => void;
}

const EditPrestataireDialog: React.FC<EditPrestataireDialogProps> = ({ open, row, onClose, onSuccess }) => {
    const translate = useTranslate();
    const isMobile = useIsMobile();
    const notify = useNotify();
    const userConnected = useSelector((state: CustomReducerState) => state.rikutec.users.userConnected);

    const [scopes, setScopes] = useState<PrestataireScope[]>([]);
    const [pdfFile, setPdfFile] = useState<File | null>(null);
    const [submitting, setSubmitting] = useState(false);
    const [showErrors, setShowErrors] = useState(false);

    useEffect(() => {
        if (!row || !open) {
            return;
        }
        setScopes([...(row.scopes || [])]);
        setPdfFile(null);
        setShowErrors(false);
        setSubmitting(false);
    }, [row, open]);

    const handleClose = () => {
        if (submitting) {
            return;
        }
        onClose();
    };

    const typesValid = scopes.length > 0;
    const departmentsValid = scopesAreValid(scopes);
    const companyAddress = row
        ? formatCompanyAddress(row.street, row.postalCode, row.city)
        : '';

    const handleSubmit = async () => {
        if (!row) {
            return;
        }
        setShowErrors(true);
        if (!typesValid || !departmentsValid) {
            return;
        }

        setSubmitting(true);
        try {
            let homologationMediaId: string | undefined;
            if (pdfFile) {
                const companyId = userConnected?.company?.id;
                const companyUri = userConnected?.company?.['@id'];
                if (!companyId) {
                    throw new Error(translate('app.prestataires.messages.missingCompany'));
                }
                homologationMediaId = await uploadHomologationPdf(pdfFile, companyId, companyUri);
            }

            await updatePrestataireAffiliation(row.affiliationId, {
                scopes,
                ...(homologationMediaId ? { homologationMediaId } : {}),
            });

            notify(translate('app.prestataires.messages.updateSuccess'), 'success');
            onSuccess();
            onClose();
        } catch (error) {
            const message = error instanceof Error ? error.message : translate('app.prestataires.messages.error');
            notify(message, 'error');
        } finally {
            setSubmitting(false);
        }
    };

    return (
        <Dialog
            open={open}
            onClose={handleClose}
            fullWidth
            fullScreen={isMobile}
            maxWidth='md'
            disableBackdropClick={submitting}
        >
            <DialogTitle>{translate('app.prestataires.edit.title')}</DialogTitle>
            <DialogContent>
                {row && (
                    <Typography variant='body2' color='textSecondary' style={{ marginBottom: 8 }}>
                        {[row.companyName, row.companyEmail].filter(Boolean).join(' · ') ||
                            row.affiliationId}
                    </Typography>
                )}

                <TypesAndDepartmentsFields
                    scopes={scopes}
                    onScopesChange={setScopes}
                    initialAddress={companyAddress}
                    disabled={submitting}
                    errorTypes={showErrors && !typesValid}
                    errorDepartments={showErrors && !departmentsValid}
                />

                <Box style={{ marginTop: 16 }}>
                    <Typography variant='subtitle2' style={{ marginBottom: 8 }}>
                        {translate(
                            row?.homologationMedia
                                ? 'app.prestataires.edit.replacePdf'
                                : 'app.prestataires.edit.addPdf',
                        )}
                    </Typography>
                    <Button variant='outlined' color='primary' component='label' disabled={submitting}>
                        {translate('app.prestataires.wizard.choosePdf')}
                        <input
                            type='file'
                            accept='application/pdf,.pdf'
                            hidden
                            onChange={(event) => setPdfFile(event.target.files?.[0] || null)}
                        />
                    </Button>
                    {pdfFile && (
                        <Typography variant='body2' style={{ marginTop: 8 }}>
                            {pdfFile.name}
                        </Typography>
                    )}
                    {row?.homologationMedia && (
                        <FormHelperText>{translate('app.prestataires.edit.replacePdfHelp')}</FormHelperText>
                    )}
                </Box>
            </DialogContent>
            <DialogActions>
                <Button onClick={handleClose} disabled={submitting}>
                    {translate('app.prestataires.actions.cancel')}
                </Button>
                <Button color='primary' variant='contained' onClick={handleSubmit} disabled={submitting}>
                    {submitting ? (
                        <CircularProgress size={20} color='inherit' />
                    ) : (
                        translate('app.prestataires.actions.save')
                    )}
                </Button>
            </DialogActions>
        </Dialog>
    );
};

export default EditPrestataireDialog;
